import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  GraduationCap,
  UserCog,
  BookOpen,
  ShieldCheck,
  CircleDollarSign,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Database,
  Server,
  Cpu,
  Cloud,
  Activity,
  Trash2,
  PlayCircle,
  Loader2,
  ListChecks,
  Eye,
  ArrowUpRight,
  Inbox,
  HardDrive,
} from 'lucide-react';
import {
  useGetAdminDashboardQuery,
  useReconcileStorageMutation,
  useGetAuditLogRecentQuery,
} from '../../api/apiSlice';
import toast from 'react-hot-toast';
import VideoJobsPanel from '../../components/admin/VideoJobsPanel';

// Лёгкие хелперы форматирования.
const fmtNum = (n) => Number(n || 0).toLocaleString('ru-RU');
const fmtKzt = (n) => `${Number(n || 0).toLocaleString('ru-RU')} ₸`;
const fmtBytes = (b) => {
  const x = Number(b || 0);
  if (x < 1024) return `${x} B`;
  if (x < 1024 ** 2) return `${(x / 1024).toFixed(1)} KB`;
  if (x < 1024 ** 3) return `${(x / 1024 ** 2).toFixed(1)} MB`;
  return `${(x / 1024 ** 3).toFixed(2)} GB`;
};
const fmtUptime = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d) return `${d}д ${h}ч`;
  if (h) return `${h}ч ${m}м`;
  return `${m} мин`;
};
const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString('ru-RU', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
      })
    : '—';

const AdminDashboard = () => {
  const { data, isLoading, isFetching, error, refetch } = useGetAdminDashboardQuery();
  const [reconcileStorage, { isLoading: isReconciling }] = useReconcileStorageMutation();
  const [lastRefreshed, setLastRefreshed] = useState(Date.now());

  const handleReconcile = async () => {
    try {
      const r = await reconcileStorage({}).unwrap();
      if (r.ok === false) {
        toast(r.message || 'Сверка уже выполняется');
        return;
      }
      const lines = [
        `Просканировано: ${r.scanned}`,
        r.sizeFixed ? `Размер скорректирован: ${r.sizeFixed}` : null,
        r.markedDeleted ? `Помечено удалёнными: ${r.markedDeleted}` : null,
        r.errors ? `Ошибок: ${r.errors}` : null,
      ].filter(Boolean).join(' · ');
      toast.success(`Сверка завершена · ${lines}`);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Не удалось выполнить сверку');
    }
  };

  // Простой троттлинг ручного рефреша — клиент не должен пинать API чаще 30с.
  const REFRESH_COOLDOWN_MS = 30_000;
  const cooldownLeft = Math.max(0, REFRESH_COOLDOWN_MS - (Date.now() - lastRefreshed));
  const canRefresh = cooldownLeft <= 0 && !isFetching;

  const handleRefresh = () => {
    if (!canRefresh) return;
    setLastRefreshed(Date.now());
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700 max-w-xl">
        {error?.data?.message || 'Не удалось загрузить дашборд'}
      </div>
    );
  }

  const d = data || {};

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Обзор платформы
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Сводка обновлена {fmtDate(d.generatedAt)} ·{' '}
            health-блок кэшируется на 30 сек на сервере
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={!canRefresh}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-800 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          {isFetching
            ? 'Обновляем…'
            : cooldownLeft > 0
            ? `Подождите ${Math.ceil(cooldownLeft / 1000)}с`
            : 'Обновить'}
        </button>
      </div>

      {/* Summary cards */}
      <Section title="Сводка">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <KpiCard icon={Users} label="Всего пользователей" value={fmtNum(d.summary?.totalUsers)} color="indigo" />
          <KpiCard icon={GraduationCap} label="Студентов" value={fmtNum(d.summary?.students)} color="blue" />
          <KpiCard icon={UserCog} label="Преподавателей" value={fmtNum(d.summary?.instructors)} color="purple" />
          <KpiCard icon={BookOpen} label="Курсов" value={fmtNum(d.summary?.totalCourses)} color="emerald" />
          <KpiCard
            icon={ShieldCheck}
            label="На модерации"
            value={fmtNum(d.summary?.pendingReviewCourses)}
            color={d.summary?.pendingReviewCourses ? 'amber' : 'gray'}
          />
          <KpiCard
            icon={CircleDollarSign}
            label="Доход за месяц"
            value={fmtKzt(d.summary?.monthlyRevenue)}
            sub={`${fmtNum(d.summary?.monthlyOrdersCount)} заказов`}
            color="rose"
          />
        </div>
      </Section>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Need attention */}
        <Section title="Требуют внимания" className="lg:col-span-1">
          <div className="space-y-3">
            <AttentionItem
              icon={ShieldCheck}
              label="Курсы на проверке"
              count={d.attention?.pendingReviewCourses}
              level={d.attention?.pendingReviewCourses ? 'warning' : 'ok'}
              to="/dashboard/admin/course-management"
            />
            <AttentionItem
              icon={XCircle}
              label="Упавшие video-jobs"
              count={d.attention?.failedVideoJobs}
              level={d.attention?.failedVideoJobs ? 'danger' : 'ok'}
            />
            <AttentionItem
              icon={Trash2}
              label="Отложенное удаление в R2"
              count={d.attention?.pendingDeleteAssets}
              level={d.attention?.pendingDeleteAssets ? 'warning' : 'ok'}
            />
          </div>
        </Section>

        {/* System health */}
        <Section title="Состояние системы" className="lg:col-span-2">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <HealthItem icon={Database} label="Postgres" ok={d.health?.db?.ok ?? d.health?.mongo?.ok} sub={d.health?.db?.status ?? d.health?.mongo?.status} />
            <HealthItem icon={Server} label="Redis" ok={d.health?.redis?.ok} sub={d.health?.redis?.status} />
            <HealthItem icon={Cloud} label="Cloudflare R2" ok={d.health?.r2?.ok} sub={d.health?.r2?.status} />
            <HealthItem
              icon={Cpu}
              label={`Worker · ${d.health?.worker?.queue || 'queue'}`}
              ok={d.health?.worker?.ok}
              sub={d.health?.worker?.status}
            />
            <HealthItem
              icon={Activity}
              label="API"
              ok={d.health?.api?.ok}
              sub={`uptime ${fmtUptime(d.health?.api?.processUptimeSec)}`}
            />
            {/* Queue counts */}
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 col-span-2 md:col-span-3">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                BullMQ · {d.health?.worker?.queue || '—'}
              </div>
              {d.health?.worker?.counts ? (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {[
                    { k: 'waiting', label: 'Waiting' },
                    { k: 'active', label: 'Active' },
                    { k: 'completed', label: 'Done' },
                    { k: 'failed', label: 'Failed' },
                    { k: 'delayed', label: 'Delayed' },
                    { k: 'paused', label: 'Paused' },
                  ].map(({ k, label }) => (
                    <QueueCell
                      key={k}
                      label={label}
                      value={d.health.worker.counts[k] ?? 0}
                      color={
                        k === 'failed'
                          ? 'red'
                          : k === 'active'
                          ? 'indigo'
                          : 'gray'
                      }
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Нет данных — Redis недоступен
                </p>
              )}
            </div>
          </div>
        </Section>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Pending courses preview */}
        <Section
          title="Курсы на модерации"
          className="lg:col-span-2"
          right={
            <Link
              to="/dashboard/admin/course-management"
              className="inline-flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Все заявки <ArrowUpRight className="w-4 h-4" />
            </Link>
          }
        >
          {d.moderation?.pendingPreview?.length ? (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
              {d.moderation.pendingPreview.map((c) => (
                <div key={c._id} className="flex items-start gap-3 p-4">
                  <div className="hidden sm:block w-16 h-12 rounded-lg overflow-hidden bg-gradient-to-br from-indigo-500 to-purple-600 shrink-0">
                    {c.coverImageUrl && (
                      <img
                        src={c.coverImageUrl}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate break-words">
                      {c.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate break-words">
                      {c.instructor?.name || c.instructor?.email || '—'} · {fmtDate(c.updatedAt || c.createdAt)}
                    </p>
                  </div>
                  <Link
                    to={`/dashboard/admin/course-management/${c._id}`}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200"
                  >
                    <Eye className="w-3.5 h-3.5" /> Открыть
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <Empty icon={Inbox} text="Нет курсов, ожидающих модерации" />
          )}
        </Section>

        {/* Video / storage */}
        <Section title="Видео и хранилище" className="lg:col-span-1">
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-4">
            {/* Active storage size — explicit DB-estimate label */}
            <div className="rounded-xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                  Активный размер · оценка по БД
                </span>
                <HardDrive className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
                {fmtBytes(d.videos?.assets?.estimatedActiveBytes)}
              </div>
              <div className="text-xs text-amber-800 dark:text-amber-200 mt-1">
                Это сумма <code>VideoAsset.size</code> для активных. Точное значение
                смотрите в Cloudflare R2 dashboard или нажмите «Сверить с R2».
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Mini label="Всего" value={fmtNum(d.videos?.assets?.total)} />
              <Mini label="Активных" value={fmtNum(d.videos?.assets?.active)} />
              <Mini label="Pending delete" value={fmtNum(d.videos?.assets?.pendingDelete)} />
              <Mini label="Удалённых" value={fmtNum(d.videos?.assets?.deleted)} />
              {d.videos?.assets?.activeMissingSize > 0 && (
                <div className="col-span-2 rounded-xl bg-red-50 dark:bg-red-950/20 px-3 py-2 text-xs text-red-700 dark:text-red-300">
                  У {fmtNum(d.videos.assets.activeMissingSize)} ассетов нет
                  записанного размера — оценка по этим записям = 0.
                </div>
              )}
            </div>

            <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <PlayCircle className="w-3.5 h-3.5" /> Job'ы сжатия
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Mini label="В очереди" value={fmtNum(d.videos?.jobs?.queued)} />
                <Mini label="В обработке" value={fmtNum(d.videos?.jobs?.processing)} />
                <Mini label="Готово" value={fmtNum(d.videos?.jobs?.ready)} />
                <Mini label="Ошибок" value={fmtNum(d.videos?.jobs?.failed)} color="red" />
              </div>
            </div>

            {/* Reconcile control */}
            <div className="pt-3 border-t border-gray-100 dark:border-gray-800 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Сверка с R2 (HEAD)
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">
                    {d.videos?.assets?.reconcile?.lastRunAt
                      ? `Последняя сверка: ${fmtDate(d.videos.assets.reconcile.lastRunAt)}`
                      : 'Сверка ещё не запускалась'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleReconcile}
                  disabled={isReconciling || d.videos?.assets?.reconcile?.inProgress}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isReconciling ? 'animate-spin' : ''}`} />
                  {isReconciling || d.videos?.assets?.reconcile?.inProgress
                    ? 'Идёт…'
                    : 'Сверить'}
                </button>
              </div>

              {d.videos?.assets?.reconcile?.lastReport && (
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 px-3 py-2 text-[11px] text-gray-600 dark:text-gray-300 space-y-0.5">
                  <div>
                    Просканировано: <b>{fmtNum(d.videos.assets.reconcile.lastReport.scanned)}</b>
                    {' · '}
                    Размер скорректирован: <b>{fmtNum(d.videos.assets.reconcile.lastReport.sizeFixed)}</b>
                  </div>
                  <div>
                    Помечено удалёнными: <b>{fmtNum(d.videos.assets.reconcile.lastReport.markedDeleted)}</b>
                    {' · '}
                    Ошибок: <b className={d.videos.assets.reconcile.lastReport.errors ? 'text-red-600 dark:text-red-400' : ''}>
                      {fmtNum(d.videos.assets.reconcile.lastReport.errors)}
                    </b>
                  </div>
                  {d.videos.assets.reconcile.lastReport.truncated && (
                    <div className="text-amber-700 dark:text-amber-300">
                      Превышен лимит партии — запустите сверку ещё раз для остатка.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1.5">
              <HardDrive className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              R2 не сканируется на каждый запрос дашборда — это бы било по
              лимитам бакета. Оценка строится по БД, точная сверка — по кнопке выше.
            </div>
          </div>
        </Section>
      </div>

      {/* Video compression jobs — real-time, manual refresh */}
      <VideoJobsPanel />

      {/* Recent orders preview (5 шт). Полный список — на /admin/orders. */}
      <Section
        title="Последние заказы"
        right={
          <Link
            to="/dashboard/admin/orders"
            className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Все заказы
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        }
      >
        {d.orders?.length ? (
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-2 text-left">Пользователь</th>
                    <th className="px-4 py-2 text-left">Курс</th>
                    <th className="px-4 py-2 text-right">Сумма</th>
                    <th className="px-4 py-2 text-left">Статус</th>
                    <th className="px-4 py-2 text-left">Дата</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {d.orders.map((o) => (
                    <tr key={o._id}>
                      <td className="px-4 py-2 max-w-[220px]">
                        <div className="truncate text-gray-900 dark:text-white">{o.user?.name || '—'}</div>
                        <div className="text-xs text-gray-500 truncate">{o.user?.email || ''}</div>
                      </td>
                      <td className="px-4 py-2 max-w-[260px]">
                        <div className="truncate text-gray-700 dark:text-gray-200">
                          {o.course?.title || '—'}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-900 dark:text-white">
                        {fmtKzt(o.amount)}
                      </td>
                      <td className="px-4 py-2">
                        <OrderStatusBadge status={o.status} />
                      </td>
                      <td className="px-4 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {fmtDate(o.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <Empty icon={ListChecks} text="Заказов пока нет" />
        )}
      </Section>

      {/* Activity feed — top-20 последних audit-событий с сервера. */}
      <Section
        title="Активность"
        right={
          <Link
            to="/dashboard/admin/audit-log"
            className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Все события <ArrowUpRight className="w-3 h-3" />
          </Link>
        }
      >
        <AuditActivityFeed />
      </Section>
    </div>
  );
};

// ─── Activity feed (top-20 audit events) ───────────────────────────
const AUDIT_TONE = {
  account: { dot: 'bg-violet-500', text: 'text-violet-700 dark:text-violet-300', bg: 'bg-violet-50 dark:bg-violet-900/20' },
  login:   { dot: 'bg-sky-500',    text: 'text-sky-700 dark:text-sky-300',       bg: 'bg-sky-50 dark:bg-sky-900/20' },
  oauth:   { dot: 'bg-blue-500',   text: 'text-blue-700 dark:text-blue-300',     bg: 'bg-blue-50 dark:bg-blue-900/20' },
  password:{ dot: 'bg-amber-500',  text: 'text-amber-700 dark:text-amber-300',   bg: 'bg-amber-50 dark:bg-amber-900/20' },
  order:   { dot: 'bg-emerald-500',text: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
  payout:  { dot: 'bg-teal-500',   text: 'text-teal-700 dark:text-teal-300',     bg: 'bg-teal-50 dark:bg-teal-900/20' },
  course:  { dot: 'bg-rose-500',   text: 'text-rose-700 dark:text-rose-300',     bg: 'bg-rose-50 dark:bg-rose-900/20' },
};
const DEFAULT_TONE = { dot: 'bg-slate-400', text: 'text-slate-700 dark:text-slate-300', bg: 'bg-slate-50 dark:bg-slate-900/20' };
const toneFor = (action) => AUDIT_TONE[String(action).split('.')[0]] || DEFAULT_TONE;

function fmtAuditTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = Date.now();
  const diffSec = Math.floor((now - d.getTime()) / 1000);
  if (diffSec < 60) return 'только что';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} мин назад`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} ч назад`;
  return d.toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const AuditActivityFeed = () => {
  const { data, isFetching, error } = useGetAuditLogRecentQuery();
  const items = data?.items || [];

  if (error) {
    return (
      <Empty icon={AlertTriangle} text="Не удалось загрузить активность" hint={error?.data?.message || ''} />
    );
  }
  if (isFetching && items.length === 0) {
    return (
      <div className="flex items-center justify-center py-6 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (items.length === 0) {
    return <Empty icon={Activity} text="Событий пока нет" hint="Когда что-то произойдёт — отобразится здесь" />;
  }

  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/60 dark:ring-slate-800 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
      {items.map((e) => {
        const tone = toneFor(e.action);
        const actorLabel = e.actor?.deleted
          ? `${e.actor.id.slice(0, 8)}… (удалён)`
          : e.actor?.name || e.actor?.email || 'System';
        const targetLabel = e.targetType
          ? `${e.targetType}${e.targetId ? `: ${String(e.targetId).slice(0, 12)}` : ''}`
          : '';
        return (
          <div key={e.id} className="flex items-start gap-3 px-4 py-3">
            <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${tone.dot}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-bold ${tone.bg} ${tone.text}`}>
                  {e.action}
                </span>
                <span className="text-sm font-medium text-slate-900 dark:text-white truncate">
                  {actorLabel}
                </span>
                {targetLabel && (
                  <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                    → {targetLabel}
                  </span>
                )}
              </div>
              {e.metadata && Object.keys(e.metadata).length > 0 && (
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400 truncate font-mono">
                  {Object.entries(e.metadata).slice(0, 2).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')}
                </p>
              )}
            </div>
            <span className="text-[11px] text-slate-400 tabular-nums shrink-0 pt-0.5">
              {fmtAuditTime(e.createdAt)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ─── building blocks ─────────────────────────────────────────────────
const Section = ({ title, children, className = '', right }) => (
  <section className={className}>
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
      {right}
    </div>
    {children}
  </section>
);

const KPI_PALETTE = {
  indigo: 'bg-indigo-100 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300',
  blue: 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300',
  purple: 'bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300',
  emerald: 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  amber: 'bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300',
  rose: 'bg-rose-100 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300',
  gray: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
};

const KpiCard = ({ icon: Icon, label, value, sub, color = 'indigo' }) => (
  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-sm">
    <div className="flex items-start justify-between mb-2">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      <span className={`p-1.5 rounded-lg ${KPI_PALETTE[color]}`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
    </div>
    <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tabular-nums truncate">{value}</div>
    {sub && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{sub}</div>}
  </div>
);

const HealthItem = ({ icon: Icon, label, ok, sub }) => {
  const cls = ok
    ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900/40'
    : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/40';
  const dot = ok ? 'bg-emerald-500' : 'bg-red-500';
  return (
    <div className={`rounded-2xl border p-4 ${cls}`}>
      <div className="flex items-center justify-between mb-1.5">
        <Icon className="w-4 h-4 text-gray-700 dark:text-gray-200" />
        <span className={`inline-block w-2 h-2 rounded-full ${dot} ${ok ? 'animate-pulse' : ''}`} />
      </div>
      <div className="text-sm font-semibold text-gray-900 dark:text-white">{label}</div>
      <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 capitalize">{sub || (ok ? 'ok' : 'down')}</div>
    </div>
  );
};

const QueueCell = ({ label, value, color = 'gray' }) => {
  const palette = {
    indigo: 'text-indigo-600 dark:text-indigo-400',
    red: 'text-red-600 dark:text-red-400',
    gray: 'text-gray-700 dark:text-gray-200',
  }[color];
  return (
    <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 px-3 py-2 text-center">
      <div className={`text-base font-bold tabular-nums ${palette}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );
};

const AttentionItem = ({ icon: Icon, label, count, level, to }) => {
  const palette = {
    ok: 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/40',
    warning: 'bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-900/40',
    danger: 'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-900/40',
  }[level];
  const Icon2 = level === 'ok' ? CheckCircle2 : level === 'warning' ? AlertTriangle : XCircle;
  const Wrapper = to ? Link : 'div';
  const wrapperProps = to ? { to } : {};
  return (
    <Wrapper
      {...wrapperProps}
      className={`block rounded-2xl border p-3 flex items-center gap-3 transition ${palette} ${to ? 'hover:shadow-sm' : ''}`}
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="text-sm font-medium flex-1 break-words">{label}</span>
      <span className="text-base font-bold tabular-nums">{count ?? 0}</span>
      <Icon2 className="w-4 h-4 shrink-0" />
    </Wrapper>
  );
};

const Mini = ({ label, value, hint, color = 'gray' }) => {
  const palette = {
    red: 'text-red-600 dark:text-red-400',
    gray: 'text-gray-900 dark:text-white',
  }[color];
  return (
    <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 px-3 py-2">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`text-base font-bold tabular-nums ${palette}`}>{value}</div>
      {hint && <div className="text-[10px] text-gray-400">{hint}</div>}
    </div>
  );
};

const OrderStatusBadge = ({ status }) => {
  const map = {
    paid: { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', label: 'Оплачен' },
    pending: { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', label: 'Ожидание' },
    cancelled: { cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300', label: 'Отменён' },
    expired: { cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400', label: 'Истёк' },
  };
  const m = map[status] || { cls: 'bg-gray-100 text-gray-700', label: status };
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${m.cls}`}>{m.label}</span>;
};

const Empty = ({ icon: Icon, text, hint }) => (
  <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-8 text-center">
    <Icon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">{text}</p>
    {hint && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{hint}</p>}
  </div>
);

export default AdminDashboard;
